import React from 'react';
import { compose } from 'recompose';
import { withApiData, ApiDataBinding } from 'react-api-data';
import { ApiResponse, CrossIxConnection } from 'constants/api';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import ViewContainer from 'components/utils/ViewContainer';
import { Row, Col, Card, CardHeader, CardBody } from 'reactstrap';
import SyncButton from 'components/utils/SyncButton';
import { DetailsTable } from 'components/styled';
import RequestStatusRenderer from 'components/utils/RequestStatusRenderer';
import PageLoading from 'components/utils/PageLoading';
import AlertMessage from 'components/utils/AlertMessage';
import NotFound from './NotFound';
import CrossIxConnectionEditModal from 'components/modals/CrossIxConnectionEditModal';

interface DetailsProps {
    crossIxConnection: CrossIxConnection;
    refetchData: () => void;
}

function CrossIxConnectionDetails(props: DetailsProps) {
    const { t } = useTranslation();
    const { crossIxConnection, refetchData } = props;
    const pageTitle = t('crossIxConnectionDetails.pageTitle', { xmlId: crossIxConnection.xml_id });

    return (
        <>
            <Helmet title={pageTitle} />
            <ViewContainer>
                <Row>
                    <Col>
                        <div className="d-flex justify-content-between">
                            <div className="d-flex align-items-center">
                                <h2>{pageTitle}</h2>
                                <SyncButton
                                    className="ml-2"
                                    title={t('crossIxConnectionDetails.refreshButton')}
                                    onClick={refetchData}
                                />
                            </div>
                            <CrossIxConnectionEditModal crossIxConnection={crossIxConnection} />
                        </div>
                        <hr />
                        <Card className="card-accent-primary">
                            <CardHeader>
                                <strong>{t('crossIxConnectionDetails.detailsTitle')}</strong>
                            </CardHeader>
                            <CardBody>
                                <DetailsTable size="sm" borderless>
                                    <tbody>
                                        <tr>
                                            <th>{t('crossIxConnection.xmlId')}</th>
                                            <td>{crossIxConnection.xml_id}</td>
                                        </tr>
                                        <tr>
                                            <th>{t('crossIxConnection.sourceExchangeName')}</th>
                                            <td>{crossIxConnection.source_exchange_name}</td>
                                        </tr>
                                        <tr>
                                            <th>{t('crossIxConnection.destinationExchangeName')}</th>
                                            <td>{crossIxConnection.destination_exchange_name}</td>
                                        </tr>
                                        <tr>
                                            <th>{t('crossIxConnection.targetVlanName')}</th>
                                            <td>{crossIxConnection.target_vlan_name}</td>
                                        </tr>
                                        <tr>
                                            <th>{t('crossIxConnection.sourceExchangeCustomerLinkVlanXmlId')}</th>
                                            <td>{crossIxConnection.source_exchange_customer_link_vlan_xml_id}</td>
                                        </tr>
                                        <tr>
                                            <th>{t('crossIxConnection.sourceExchangeTransportLinkVlanXmlId')}</th>
                                            <td>{crossIxConnection.source_exchange_transport_link_vlan_xml_id}</td>
                                        </tr>
                                        <tr>
                                            <th>{t('crossIxConnection.destinationExchangeCustomerLinkVlanXmlId')}</th>
                                            <td>{crossIxConnection.destination_exchange_customer_link_vlan_xml_id}</td>
                                        </tr>
                                        <tr>
                                            <th>{t('crossIxConnection.destinationExchangeLinkVlanIpv4Address')}</th>
                                            <td>{crossIxConnection.destination_exchange_link_vlan_ipv4_address}</td>
                                        </tr>
                                        <tr>
                                            <th>{t('crossIxConnection.destinationExchangeLinkVlanIpv6Address')}</th>
                                            <td>{crossIxConnection.destination_exchange_link_vlan_ipv6_address}</td>
                                        </tr>
                                        <tr>
                                            <th>{t('crossIxConnection.note')}</th>
                                            <td style={{ whiteSpace: 'pre-wrap' }}>{crossIxConnection.note}</td>
                                        </tr>
                                    </tbody>
                                </DetailsTable>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </ViewContainer>
        </>
    );
}

interface WrapperInProps {
    uuid: string;
}

interface WrapperEnhanceProps {
    crossIxConnectionBinding: ApiDataBinding<ApiResponse<CrossIxConnection>>;
}

type WrapperProps = WrapperInProps & WrapperEnhanceProps;

const enhance = compose<WrapperProps, WrapperInProps>(
    withApiData(
        {
            crossIxConnectionBinding: 'getCrossIxConnection',
        },
        (props: WrapperInProps) => ({
            crossIxConnectionBinding: { uuid: props.uuid },
        })
    )
);

function Wrapper(props: WrapperProps) {
    const { crossIxConnectionBinding } = props;
    const { t } = useTranslation();

    return (
        <RequestStatusRenderer
            request={crossIxConnectionBinding}
            loading={<PageLoading />}
            success={() => (
                <CrossIxConnectionDetails
                    crossIxConnection={crossIxConnectionBinding.data!.data!}
                    refetchData={crossIxConnectionBinding.invalidateCache}
                />
            )}
            failed={() => {
                if (crossIxConnectionBinding.request.response!.status === 404) {
                    return <NotFound />;
                } else {
                    return <AlertMessage message={t('notification.unexpectedError')} />;
                }
            }}
        />
    );
}

export default enhance(Wrapper);
